import { useState, useEffect, useMemo } from 'react';
import { Save, Eye, Code, AlertCircle, CheckCircle } from 'lucide-react';
import SimpleMDEEditor from 'react-simplemde-editor';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SiteContentService, SiteContent } from '../services/SiteContentService';
import 'easymde/dist/easymde.min.css';

export default function AdminDataDeletion() {
  const [content, setContent] = useState<SiteContent | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'visual' | 'source'>('visual');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const data = await SiteContentService.getContentByKey('data_deletion');
      if (data) {
        setContent(data);
        setEditedContent(data.content);
        setEditedTitle(data.title);
      }
    } catch (error) {
      console.error('Error loading data deletion policy:', error);
      setMessage({ type: 'error', text: 'Failed to load data deletion policy' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content) return;

    try {
      setSaving(true);
      setMessage(null);

      await SiteContentService.updateContent('data_deletion', {
        title: editedTitle,
        content: editedContent,
      });

      setMessage({ type: 'success', text: 'Data deletion policy saved successfully!' });

      await loadContent();
    } catch (error) {
      console.error('Error saving data deletion policy:', error);
      setMessage({ type: 'error', text: 'Failed to save data deletion policy' });
    } finally {
      setSaving(false);
    }
  };

  const editorOptions = useMemo(() => {
    return {
      spellChecker: false,
      placeholder: 'Enter data deletion instructions in Markdown...',
      status: false,
      toolbar: [
        'bold',
        'italic',
        'heading',
        '|',
        'quote',
        'unordered-list',
        'ordered-list',
        '|',
        'link',
        'image',
        '|',
        'preview',
        'side-by-side',
        'fullscreen',
        '|',
        'guide',
      ],
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Data deletion policy not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
          <div className="p-6 border-b border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-white">Edit Data Deletion Instructions</h1>
              <div className="flex items-center gap-3">
                <div className="flex bg-white/10 rounded-lg p-1">
                  <button
                    onClick={() => setMode('visual')}
                    className={`px-4 py-2 rounded-md flex items-center gap-2 transition-colors ${
                      mode === 'visual'
                        ? 'bg-blue-500 text-white'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    Visual
                  </button>
                  <button
                    onClick={() => setMode('source')}
                    className={`px-4 py-2 rounded-md flex items-center gap-2 transition-colors ${
                      mode === 'source'
                        ? 'bg-blue-500 text-white'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    <Code className="w-4 h-4" />
                    Source
                  </button>
                </div>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {message && (
              <div
                className={`flex items-center gap-2 px-4 py-3 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                    : 'bg-red-500/20 text-red-300 border border-red-500/30'
                }`}
              >
                {message.type === 'success' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                {message.text}
              </div>
            )}

            <div className="mt-4">
              <label className="block text-white text-sm font-medium mb-2">
                Title
              </label>
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Data Deletion Instructions"
              />
            </div>
          </div>

          <div className="p-6">
            {mode === 'visual' ? (
              <div className="markdown-editor">
                <SimpleMDEEditor
                  value={editedContent}
                  onChange={setEditedContent}
                  options={editorOptions}
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Markdown Source
                  </label>
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-[600px] px-4 py-3 bg-slate-800 border border-white/20 rounded-lg text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Enter Markdown content..."
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Preview
                  </label>
                  <div className="h-[600px] overflow-y-auto bg-kod-purple-800/50 backdrop-blur-sm border border-white/10 rounded-lg p-6">
                    <div className="prose prose-invert prose-lg max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ children }) => <h2 className="text-3xl font-bold mb-4 text-white">{children}</h2>,
                          h2: ({ children }) => <h3 className="text-2xl font-bold mb-4 mt-8 text-white">{children}</h3>,
                          h3: ({ children }) => <h4 className="text-xl font-bold mb-3 mt-6 text-white">{children}</h4>,
                          p: ({ children }) => <p className="text-white/80 leading-relaxed mb-4">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc list-inside text-white/80 mb-4 space-y-2">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside text-white/80 mb-4 space-y-2">{children}</ol>,
                          li: ({ children }) => <li className="ml-4">{children}</li>,
                          a: ({ href, children }) => (
                            <a href={href} className="text-blue-400 hover:underline">
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {editedContent}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 bg-white/5 border-t border-white/20">
            <div className="text-sm text-white/60">
              Last updated: {new Date(content.updated_at).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
